/**
 * @file tier-discount.controller.ts
 * @description Complete Tier Discount Controller.
 * Fixes validation errors when adding "Default Title" variants by normalizing parameter objects.
 */

import { Controller } from 'framework/core';
import { Injectable } from 'framework/di';
import { Signal, signal } from 'framework/store/signal';

import { AppService } from '../app/app.service';
import { CartController } from '../cart/cart.controller';
import { CartService } from '../cart/cart.service';
import { TierDiscountAPI } from './tier-discount.api';
import { TierDiscountModel } from './tier-discount.model';
import { TierDiscountService } from './tier-discount.service';

@Injectable
export class TierDiscountWidgetController extends Controller {
  state: Signal<{ loading: boolean; model: TierDiscountModel | null }>;

  // Keep bundleRequestMap to prevent redundant API calls for the same bundle on Search/Collection pages
  protected static readonly bundleRequestMap = new Map<string, Promise<any>>();
  protected _lastUiSignature = '';
  protected _syncDebounceTimer: NodeJS.Timeout | null = null;
  protected _changeQtyTimer: NodeJS.Timeout | null = null;

  constructor(
    protected appService: AppService,
    protected tierDiscountService: TierDiscountService,
    protected cartService: CartService,
    protected tierDiscountAPI: TierDiscountAPI,
    protected cartController: CartController
  ) {
    super();
    this.state = signal({ loading: false, model: null });
  }

  async connect(props: { widgetId: string }, parent: HTMLElement): Promise<void> {
    if (parent.dataset.tierConnected === 'true') return;
    parent.dataset.tierConnected = 'true';

    super.connect(props, parent);
    if (!this.tierDiscountService.isValidBundle(props.widgetId)) return;

    await this.safeInitWidget(props.widgetId, parent);
    this.registerTierEvents(parent);

    // Sync UI whenever the cart is updated by the Boost Cart App
    const syncWithApp = this.debounce(() => {
      void this.syncWithCart();
    }, 500);

    window.addEventListener('boost-sd-cart-updated', syncWithApp);
  }

  protected formatMilestoneLabels(model: TierDiscountModel, rate: number): void {
    if (!this.container) return;
    const thresholdType = model.properties.threshold_type || 'quantity';

    this.container
      .querySelectorAll('.boost-sd__tier-discount-milestone')
      .forEach((el: any, index: number) => {
        const discount = model.properties.discounts[index];
        if (!discount) return;

        const labelEl = el.querySelector('.boost-sd__tier-discount-milestone-label');
        const discountEl = el.querySelector('.boost-sd__tier-discount-milestone-discount');

        if (labelEl) {
          if (thresholdType === 'subtotal') {
            labelEl.textContent = `BUY ${this.appService.formatCurrency(Number(discount.item) * rate)}`;
          } else {
            labelEl.textContent = `BUY ${discount.item} ITEMS`;
          }
        }

        if (discountEl) {
          if (discount.discount_type === 'amount') {
            const val = parseFloat(discount.discount_value) * rate;
            discountEl.textContent = `GET ${this.appService.formatCurrency(val)} OFF`;
          } else {
            discountEl.textContent = `GET ${discount.discount_value}% OFF`;
          }
        }
      });
  }

  protected async safeInitWidget(widgetId: string, container: HTMLElement): Promise<void> {
    const currentProductId = this.getProductId();
    const cacheKey = `${widgetId}-${currentProductId}`;
    let bundleData: any;

    if (TierDiscountWidgetController.bundleRequestMap.has(cacheKey)) {
      bundleData = await TierDiscountWidgetController.bundleRequestMap.get(cacheKey);
    } else {
      const fetchPromise = this.tierDiscountService.fetchBundle(widgetId, {
        productId: currentProductId,
      });
      TierDiscountWidgetController.bundleRequestMap.set(cacheKey, fetchPromise);
      try {
        bundleData = await fetchPromise;
      } catch (e) {
        TierDiscountWidgetController.bundleRequestMap.delete(cacheKey);
        return;
      }
    }

    if (bundleData) {
      const currencyRate = Number(
        (window as any).Shopify?.currency?.rate || bundleData.currency_rate || 1
      );
      const processedData = this.applySmartPricing(bundleData);
      // Find the product index that matches the current page
      if (currentProductId && processedData.products) {
        const products = processedData.products;
        const currentIndex = products.findIndex(
          (p: any) => Number(p.id) === Number(currentProductId)
        );

        if (currentIndex > -1) {
          const currentProduct = products.splice(currentIndex, 1)[0];
          products.unshift(currentProduct);
        }
      }

      const model = this.tierDiscountService.createModel(processedData);
      (model.properties as any).currency_rate = currencyRate;
      this.state = signal({ loading: false, model });

      const template = await this.tierDiscountService.getTemplate(
        model.properties.widgetId || widgetId
      );
      if (template && container) {
        const html = await this.appService.templateRender(template, {
          bundle: model.properties,
          thresholdType: model.properties.threshold_type,
          widgetDesignSettings: model.properties.widgetDesignSettings,
          locale: this.getLocale(),
          currencyRate: currencyRate,
        });

        // Clear container and safely append parsed HTML content
        container.textContent = '';
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const elements = doc.body.children;
        Array.from(elements).forEach((element) => {
          container.appendChild(element.cloneNode(true));
        });

        this.formatMilestoneLabels(model, currencyRate);
        this.formatProductPrices(model);
        this.hideArrowForSingleDefaultVariants(container);
      }

      await this.initModal(model);
      void this.syncWithCart();
    }
  }

  protected syncWithCart = async (): Promise<void> => {
    try {
      const cart = await this.cartService.getCartData();
      this.updateUIData(cart);
    } catch (e) {
      this.appService.logger.error('TierDiscount > Sync failed', e);
    }
  };

  protected debouncedSyncWithCart(): void {
    if (this._syncDebounceTimer) {
      clearTimeout(this._syncDebounceTimer);
    }
    this._syncDebounceTimer = setTimeout(() => {
      void this.syncWithCart();
      this._syncDebounceTimer = null;
    }, 300); // 300ms debounce
  }

  protected updateUIData = (cart: any): void => {
    if (!this.container || !cart || !this.state.value.model) return;
    const model = this.state.value.model;

    const bundleProductIds = model.properties.products.map((p) => p.id);

    const relevantItems = cart.items.filter((item: any) => {
      // Must be a product that belongs to this tiered bundle.
      if (!bundleProductIds.includes(Number(item.product_id))) return false;

      // Exclude items that were added via any other bundle (classic, AI, etc.).
      // Those items carry _boostBundleId in their cart line properties.
      // Items added directly through this tier-discount widget have no _boostBundleId
      // (tier discount adds via CartAPI.add without bundle properties).
      const itemProps = item.properties || {};
      if (itemProps['_boostBundleId']) return false;

      return true;
    });

    model.properties.products.forEach((product) => {
      const variantsInCart = relevantItems.filter(
        (i) => Number(i.product_id) === Number(product.id)
      );
      this.renderAddedVariantList(product.id, variantsInCart);
    });

    this.syncModalState(model, relevantItems);

    let totalQty = 0;
    let totalOriginalCents = 0;
    relevantItems.forEach((item: any) => {
      totalQty += item.quantity;
      totalOriginalCents += item.price * item.quantity;
    });

    const { discountedTotalCents, hasDiscount } = this.calculateDiscountedTotal(
      model,
      totalQty,
      totalOriginalCents
    );

    this.refreshDiscountStatus(model, totalQty, totalOriginalCents);
    this.updatePriceSummaryUI(
      totalQty,
      totalOriginalCents,
      discountedTotalCents,
      hasDiscount,
      relevantItems
    );
    this.updateModalFooter(discountedTotalCents, totalOriginalCents, hasDiscount);
    this.updateCheckoutButtonState(totalQty);
  };

  protected calculateDiscountedTotal(
    model: TierDiscountModel,
    totalQty: number,
    originalCents: number
  ) {
    const rate = (model.properties as any).currency_rate || 1;
    const thresholdType = model.properties.threshold_type || 'quantity';
    const compareValue = thresholdType === 'subtotal' ? originalCents : totalQty;

    let appliedDiscount: any = null;
    const sortedDiscounts = [...model.properties.discounts].sort((a, b) => b.item - a.item);

    for (const discount of sortedDiscounts) {
      const threshold = thresholdType === 'subtotal' ? discount.item * rate * 100 : discount.item;

      if (compareValue >= threshold) {
        appliedDiscount = discount;
        break;
      }
    }

    if (!appliedDiscount) return { discountedTotalCents: originalCents, hasDiscount: false };

    let discountedTotal = originalCents;
    const val = parseFloat(appliedDiscount.discount_value || '0');

    if (appliedDiscount.discount_type === 'percentage') {
      discountedTotal = originalCents * (1 - val / 100);
    } else if (appliedDiscount.discount_type === 'amount') {
      discountedTotal = Math.max(0, originalCents - val * rate * 100);
    }

    return { discountedTotalCents: Math.round(discountedTotal), hasDiscount: true };
  }

  protected updatePriceSummaryUI(
    totalQty: number,
    originalCents: number,
    totalCents: number,
    hasDiscount: boolean,
    items: any[]
  ) {
    if (!this.container) return;

    const countEl = this.container.querySelector('.boost-sd__tier-discount-items-count');
    if (countEl) {
      countEl.textContent = totalQty > 0 ? `${totalQty} item(s) added` : 'No items added';
      countEl.classList.toggle('boost-sd__tier-discount-items-count--empty', totalQty === 0);
    }
    const priceEl = this.container.querySelector('.boost-sd__tier-discount-total-price');
    const originalPriceEl = this.container.querySelector(
      '.boost-sd__tier-discount-total-original'
    ) as HTMLElement;

    if (priceEl) priceEl.textContent = this.appService.formatCurrency(totalCents / 100);
    if (originalPriceEl) {
      originalPriceEl.textContent = hasDiscount
        ? this.appService.formatCurrency(originalCents / 100)
        : '';
      originalPriceEl.style.display = hasDiscount ? 'inline-block' : 'none';
    }

    const seeAllBtn = this.container.querySelector(
      '.boost-sd__tier-discount-items-link'
    ) as HTMLElement;
    if (seeAllBtn) seeAllBtn.style.display = totalQty > 0 ? 'inline-block' : 'none';

    const thumbContainer = this.container.querySelector(
      '.boost-sd__tier-discount-items-thumbnails'
    );
    if (thumbContainer) {
      const htmlContent =
        items.length > 0
          ? items
              .slice(0, 4)
              .map(
                (i: any) => `<img src="${i.image}" class="boost-sd__tier-discount-item-thumb" />`
              )
              .join('') +
            (items.length > 4
              ? `<span class="boost-sd__tier-discount-item-more">+${items.length - 4}</span>`
              : '')
          : '';
      this.safeSetHTML(thumbContainer, htmlContent);
    }
  }

  protected updateCheckoutButtonState(totalQty: number): void {
    if (!this.container) return;

    const checkoutButtons = this.container.querySelectorAll('.boost-sd__tier-discount-checkout');
    checkoutButtons.forEach((btn) => {
      const button = btn as HTMLButtonElement;
      if (totalQty === 0) {
        button.setAttribute('disabled', 'true');
        button.classList.add('boost-sd__tier-discount-checkout--disabled');
      } else {
        button.removeAttribute('disabled');
        button.classList.remove('boost-sd__tier-discount-checkout--disabled');
      }
    });
  }

  /**
   * Navigates to a product URL with locale-aware path building.
   * Matches the behaviour of handleClickTagAItemBuildUrlWithLocale used by the classic bundle.
   */

  protected navigateWithLocale(href: string): void {
    // Block non-HTTP(S) protocols (e.g. javascript:, data:) to prevent XSS.
    const normalised = href.trim().toLowerCase();
    if (
      !normalised.startsWith('https://') &&
      !normalised.startsWith('http://') &&
      !normalised.startsWith('/')
    ) {
      return;
    }

    const routesRoot = ((window as any)?.Shopify?.routes?.root as string) || '/';
    if (href.startsWith('https://') || href.startsWith(routesRoot)) {
      window.location.href = href;
    } else {
      const path = href.startsWith('/') ? href.slice(1) : href;
      window.location.href = `${routesRoot}${path}`;
    }
  }

  protected registerTierEvents(container: HTMLElement): void {
    // Prevent duplicate event listeners when connect() is called again after reinitialize
    if (container.dataset.tierEventsBound === 'true') return;
    container.dataset.tierEventsBound = 'true';

    container.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;

      // 0. Product link navigation with locale support (image link and product name link)
      const productLink = target.closest(
        '.boost-sd__tier-discount-product-image, .boost-sd__tier-discount-product-name'
      ) as HTMLAnchorElement | null;
      if (productLink) {
        const href = productLink.getAttribute('href');
        if (href) {
          event.preventDefault();
          this.navigateWithLocale(href);
        }
        return;
      }

      // 1. Add Button Logic (Direct Add or Toggle Dropdown)
      const addBtn = target.closest('.boost-sd__tier-discount-add-dropdown') as HTMLElement;
      if (addBtn) {
        const wrapper = addBtn.closest('.boost-sd__tier-discount-dropdown-wrapper');
        const menu = wrapper?.querySelector(
          '.boost-sd__tier-discount-dropdown-menu'
        ) as HTMLElement;

        // Check if this product has only Default Title variant
        const productId = addBtn.dataset.productId;
        const hasOnlyDefaultTitle = this.hasOnlyDefaultTitleVariant(productId);

        if (hasOnlyDefaultTitle && menu) {
          // Get the single variant ID and add directly to cart
          const defaultVariantItem = menu.querySelector(
            '.boost-sd__tier-discount-dropdown-item'
          ) as HTMLElement;
          const variantId = defaultVariantItem?.dataset.variantId;

          if (variantId) {
            void this.cartController
              .addToCart({ productId: variantId, quantity: 1 }, addBtn, true)
              .then(() => {
                this.debouncedSyncWithCart();

                // GA4: boost_add_to_cart — tiered bundle (default-title direct add)
                window.dispatchEvent(
                  new CustomEvent('boost-sd-tier-discount-add-to-cart', {
                    detail: {
                      productId: productId || '',
                      variantId,
                      widgetId: this.state.value.model?.properties.widgetId || '',
                    },
                  })
                );
              });
          }
          return;
        }

        // Original dropdown toggle logic for products with multiple variants
        if (menu) {
          const isOpen = menu.style.display === 'block';
          // Close all other menus before opening the current one
          container.querySelectorAll('.boost-sd__tier-discount-dropdown-menu').forEach((m: any) => {
            (m as HTMLElement).style.display = 'none';
          });
          menu.style.display = isOpen ? 'none' : 'block';
        }
        return;
      }

      // 2. Add To Cart Logic (For Variant Item or Default Title)
      const dropdownItem = target.closest('.boost-sd__tier-discount-dropdown-item') as HTMLElement;
      if (dropdownItem) {
        const vId = dropdownItem.dataset.variantId;
        if (vId) {
          // Use CartController.addToCart:
          // - Parameter 2 (dropdownItem) handles "Adding..." -> "Added!" text state
          // - Parameter 3 (true) automatically opens the Boost App Cart
          void this.cartController
            .addToCart({ productId: vId, quantity: 1 }, dropdownItem, true)
            .then(() => {
              this.debouncedSyncWithCart();

              // GA4: boost_add_to_cart — tiered bundle (dropdown variant add)
              const productIdForGa4 =
                (dropdownItem.closest('[data-product-id]') as HTMLElement | null)?.dataset
                  ?.productId || '';
              window.dispatchEvent(
                new CustomEvent('boost-sd-tier-discount-add-to-cart', {
                  detail: {
                    productId: productIdForGa4,
                    variantId: vId,
                    widgetId: this.state.value.model?.properties.widgetId || '',
                  },
                })
              );

              // Delay closing menu to let user see "Added!" text
              setTimeout(() => {
                container
                  .querySelectorAll('.boost-sd__tier-discount-dropdown-menu')
                  .forEach((m: any) => {
                    (m as HTMLElement).style.display = 'none';
                  });
              }, 800);
            });
        }
        return;
      }

      // 3. Change Quantity Logic (Plus/Minus under Add button)
      const qtyBtn = target.closest('.boost-sd__tier-discount-quantity-btn') as HTMLElement;
      if (qtyBtn) {
        const vId = qtyBtn.dataset.variantId;
        const action = qtyBtn.dataset.action;
        const input = container.querySelector(
          `.boost-sd__tier-discount-variant[data-variant-id="${vId}"] .boost-sd__tier-discount-quantity-input`
        ) as HTMLInputElement;

        if (vId && input) {
          let qty = parseInt(input.value || '0');
          qty = action === 'increase' ? qty + 1 : Math.max(0, qty - 1);

          // Use CartService.changeQuantity to leverage the internal queue
          void this.cartService.changeQuantity(vId, qty).then(() => {
            this.debouncedSyncWithCart();
          });
        }
        return;
      }

      // 4. Checkout Button Logic
      const checkoutBtn = target.closest('.boost-sd__tier-discount-checkout-btn');
      if (checkoutBtn) {
        void this.cartService.getCartData().then((cartData: any) => {
          if (cartData && cartData.item_count > 0) {
            window.location.href = '/checkout';
          } else {
            this.cartController.openCart();
          }
        });
        return;
      }

      // 5. Open Modal Logic (See all link)
      if (target.closest('.boost-sd__tier-discount-items-link')) {
        const modal = document.querySelector(
          `.boost-sd__tier-discount-modal[data-widget-id="${this.state.value.model?.properties.widgetId}"]`
        ) as HTMLElement;
        if (modal) {
          modal.style.display = 'block';
          document.body.style.overflow = 'hidden';
        }
      }
    });

    // Close dropdown when clicking outside the widget
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.boost-sd__tier-discount-dropdown-wrapper')) {
        container.querySelectorAll('.boost-sd__tier-discount-dropdown-menu').forEach((m: any) => {
          (m as HTMLElement).style.display = 'none';
        });
      }
    });
  }

  protected debouncedChangeQuantity = (variantId: string, quantity: number): void => {
    if (this._changeQtyTimer) {
      clearTimeout(this._changeQtyTimer);
    }

    this._changeQtyTimer = setTimeout(() => {
      void this.cartService.changeQuantity(variantId, quantity).then(() => {
        void this.syncWithCart();
      });
      this._changeQtyTimer = null;
    }, 500);
  };

  protected async initModal(model: TierDiscountModel): Promise<void> {
    const templateKey = 'tierDiscountBundlePopup';
    const template = await this.tierDiscountService.getTemplate(
      model.properties.widgetId,
      templateKey
    );
    if (!template) return;

    const html = await this.appService.templateRender(template, {
      bundle: model.properties,
      locale: this.getLocale(),
      widgetDesignSettings: model.properties.widgetDesignSettings,
      currencyRate: (model.properties as any).currency_rate || 1,
    });

    const oldModal = document.querySelector(
      `.boost-sd__tier-discount-modal[data-widget-id="${model.properties.widgetId}"]`
    );
    oldModal?.remove();

    const div = document.createElement('div');
    this.safeSetHTML(div, html);
    const modal = div.firstElementChild as HTMLElement;
    if (!modal) return;

    document.body.appendChild(modal);

    void this.cartService.getCartData().then((cart) => this.updateUIData(cart));

    modal.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      const vId = t.closest('[data-variant-id]')?.getAttribute('data-variant-id');

      if (
        t.closest('.boost-sd__tier-discount-modal-close') ||
        t.closest('.boost-sd__tier-discount-modal-overlay')
      ) {
        modal.style.display = 'none';
        document.body.style.overflow = '';
        return;
      }

      if (vId) {
        if (t.closest('.boost-sd__tier-discount-quantity-btn')) {
          const action = (t.closest('.boost-sd__tier-discount-quantity-btn') as HTMLElement).dataset
            .action;
          const input = modal.querySelector(
            `.js-v-qty[data-variant-id="${vId}"]`
          ) as HTMLInputElement;
          if (input) {
            let qty = parseInt(input.value || '0');
            qty = action === 'increase' ? qty + 1 : Math.max(0, qty - 1);
            input.value = qty.toString(); // Cập nhật UI tạm thời cho mượt
            this.debouncedChangeQuantity(vId, qty);
          }
        } else if (t.closest('.js-v-delete')) {
          this.debouncedChangeQuantity(vId, 0);
        }
      }

      if (t.closest('.boost-sd__tier-discount-checkout-btn')) {
        window.location.href = '/checkout';
      }
    });
  }
  protected renderAddedVariantList(productId: number, variantsInCart: any[]): void {
    const listContainer = this.container?.querySelector(
      `.boost-sd__tier-discount-added-variants[data-product-id="${productId}"]`
    );
    if (!listContainer) return;
    if (variantsInCart.length === 0) {
      listContainer.textContent = '';
      return;
    }
    const htmlContent = variantsInCart
      .map(
        (v) => `
      <div class="boost-sd__tier-discount-variant" data-variant-id="${v.variant_id}">
        <div class="boost-sd__tier-discount-variant-info">
          <span class="boost-sd__tier-discount-variant-name">${this.getVariantDisplayTitle(v)}</span>
          <span class="boost-sd__tier-discount-variant-price">${this.appService.formatCurrency(v.price / 100)}</span>
        </div>
        <div class="boost-sd__tier-discount-variant-quantity">
          <button type="button" class="boost-sd__tier-discount-quantity-btn" data-action="decrease" data-variant-id="${v.variant_id}">
            <svg width="12" height="2" viewBox="0 0 12 2" fill="none"><path d="M0 1H12" stroke="currentColor" stroke-width="2"/></svg>
          </button>
          <input type="number" class="boost-sd__tier-discount-quantity-input" value="${v.quantity}" min="0" data-variant-id="${v.variant_id}" />
          <button type="button" class="boost-sd__tier-discount-quantity-btn" data-action="increase" data-variant-id="${v.variant_id}">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 0V12M0 6H12" stroke="currentColor" stroke-width="2"/></svg>
          </button>
        </div>
      </div>`
      )
      .join('');
    this.safeSetHTML(listContainer, htmlContent);
  }

  protected getVariantDisplayTitle(item: any): string {
    const title = item.variant_title || item.title || '';
    if (title === 'Default Title') return '';
    return title;
  }

  protected getDecreaseButtonIcon(quantity: number): string {
    const deviceTypes = this.appService.detectDeviceByWidth();
    const isMobile = deviceTypes.includes('mobile');

    if (isMobile && quantity === 1) {
      return `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M12.666 3.9987H10.666V2.66536C10.666 1.93003 10.068 1.33203 9.33268 1.33203H6.66602C5.93068 1.33203 5.33268 1.93003 5.33268 2.66536V3.9987H3.33268C2.96402 3.9987 2.66602 4.29736 2.66602 4.66536C2.66602 5.03336 2.96402 5.33203 3.33268 5.33203V13.9987C3.33268 14.3667 3.63068 14.6654 3.99935 14.6654H11.9993C12.368 14.6654 12.666 14.3667 12.666 13.9987V5.33203C13.0347 5.33203 13.3327 5.03336 13.3327 4.66536C13.3327 4.29736 13.0347 3.9987 12.666 3.9987ZM4.66602 13.332H11.3327V5.33203H4.66602V13.332ZM6.66602 3.9987H9.33268V2.66536H6.66602V3.9987ZM6.66602 11.9987C7.03468 11.9987 7.33268 11.7 7.33268 11.332V7.33203C7.33268 6.96403 7.03468 6.66536 6.66602 6.66536C6.29735 6.66536 5.99935 6.96403 5.99935 7.33203V11.332C5.99935 11.7 6.29735 11.9987 6.66602 11.9987ZM9.33268 11.9987C9.70135 11.9987 9.99935 11.7 9.99935 11.332V7.33203C9.99935 6.96403 9.70135 6.66536 9.33268 6.66536C8.96402 6.66536 8.66602 6.96403 8.66602 7.33203V11.332C8.66602 11.7 8.96402 11.9987 9.33268 11.9987Z" fill="#5C5F62"></path>
              </svg>`;
    } else {
      return `<svg width="12" height="2" viewBox="0 0 12 2" fill="none"><path d="M0 1H12" stroke="currentColor" stroke-width="2"/></svg>`;
    }
  }

  protected syncModalState(model: TierDiscountModel, relevantItems: any[]): void {
    const modal = document.querySelector(
      `.boost-sd__tier-discount-modal[data-widget-id="${model.properties.widgetId}"]`
    );
    if (!modal) return;

    const template = modal.querySelector('template') as HTMLTemplateElement;
    const bundleProductIds = model.properties.products.map((p) => p.id);
    const itemsByProduct = relevantItems.reduce((acc: any, item: any) => {
      const pid = Number(item.product_id);
      if (!acc[pid]) acc[pid] = [];
      acc[pid].push(item);
      return acc;
    }, {});

    bundleProductIds.forEach((pid) => {
      const variants = itemsByProduct[pid] || [];
      const productEl = modal.querySelector(
        `.boost-sd__tier-discount-modal-product[data-product-id="${pid}"]`
      ) as HTMLElement;
      const plusEl = productEl?.previousElementSibling as HTMLElement;

      if (variants.length > 0) {
        if (productEl) productEl.style.setProperty('display', 'block');

        const vListContainer = modal.querySelector(
          `.boost-sd__tier-discount-modal-variants-list[data-product-id="${pid}"]`
        );
        if (vListContainer && template) {
          vListContainer.textContent = '';
          variants.forEach((v: any) => {
            const clone = template.content.cloneNode(true) as DocumentFragment;
            const titleEl = clone.querySelector('.js-v-title') as HTMLElement;
            if (titleEl) titleEl.textContent = this.getVariantDisplayTitle(v);

            const priceEl = clone.querySelector('.js-v-price') as HTMLElement;
            if (priceEl) priceEl.textContent = this.appService.formatCurrency(v.price / 100);

            const qtyInput = clone.querySelector('.js-v-qty') as HTMLInputElement;
            if (qtyInput) {
              qtyInput.value = v.quantity.toString();
              qtyInput.setAttribute('data-variant-id', v.variant_id.toString());
            }

            const totalEl = clone.querySelector('.js-v-total') as HTMLElement;
            if (totalEl)
              totalEl.textContent = this.appService.formatCurrency((v.price * v.quantity) / 100);

            clone
              .querySelectorAll('button')
              .forEach((btn) => btn.setAttribute('data-variant-id', v.variant_id.toString()));
            vListContainer.appendChild(clone);
          });
        }

        const subtotalEl = modal.querySelector(`[data-subtotal-pid="${pid}"]`);
        if (subtotalEl) {
          const total = variants.reduce((sum: number, v: any) => sum + v.price * v.quantity, 0);
          subtotalEl.textContent = this.appService.formatCurrency(total / 100);
        }
      } else {
        if (productEl) productEl.style.display = 'none';
        if (plusEl && plusEl.classList.contains('boost-sd__tier-discount-modal-plus')) {
          plusEl.style.display = 'none';
        }
      }
    });

    const badge = modal.querySelector('.boost-sd__modal-item-count-badge');
    const totalQty = relevantItems.reduce((acc, curr) => acc + curr.quantity, 0);
    if (badge) badge.textContent = totalQty > 0 ? `(${totalQty} items)` : '';
  }

  protected updateModalFooter(
    discountedTotal: number,
    originalTotal: number,
    hasDiscount: boolean
  ): void {
    const modal = document.querySelector(`.boost-sd__tier-discount-modal`);
    if (!modal) return;

    const totalPriceEl = modal.querySelector('.boost-sd__tier-discount-modal-total-price');
    const originalPriceEl = modal.querySelector(
      '.boost-sd__tier-discount-modal-total-original'
    ) as HTMLElement;

    if (totalPriceEl)
      totalPriceEl.textContent = this.appService.formatCurrency(discountedTotal / 100);
    if (originalPriceEl) {
      originalPriceEl.textContent = hasDiscount
        ? this.appService.formatCurrency(originalTotal / 100)
        : '';
      originalPriceEl.style.display = hasDiscount ? 'inline-block' : 'none';
    }
  }

  protected refreshDiscountStatus(
    model: TierDiscountModel,
    totalQty: number,
    totalOriginalCents: number
  ): void {
    if (!this.container) return;

    const rate = (model.properties as any).currency_rate || 1;
    const thresholdType = model.properties.threshold_type || 'quantity';
    const currentValue = thresholdType === 'subtotal' ? totalOriginalCents : totalQty;
    const progressEl = this.container.querySelector(
      '.boost-sd__tier-discount-progress'
    ) as HTMLElement;
    if (!progressEl) return;

    const textAchieved =
      progressEl.dataset.textAchieved || 'Congrats! You have received a discount';
    const textLocked = progressEl.dataset.textLocked || 'Unlock a discount by adding items to cart';

    let hasDiscount = false;

    this.container.querySelectorAll('.boost-sd__tier-discount-milestone').forEach((el: any) => {
      const thresholdRaw = parseFloat(el.dataset.itemQuantity || '0');
      const threshold = thresholdType === 'subtotal' ? thresholdRaw * rate * 100 : thresholdRaw;

      const isAchieved = currentValue >= threshold;
      el.classList.toggle('boost-sd__tier-discount-milestone--achieved', isAchieved);

      if (isAchieved) hasDiscount = true;
    });

    const subTextEl = this.container.querySelector('.boost-sd__tier-discount-sub-text');
    if (subTextEl) {
      subTextEl.textContent = hasDiscount ? textAchieved : textLocked;
      subTextEl.classList.toggle('boost-sd__tier-discount-has-discount', hasDiscount);
      subTextEl.classList.toggle('boost-sd__tier-discount-no-discount', !hasDiscount);
    }
  }

  protected applySmartPricing(data: any): any {
    const urlParams = new URLSearchParams(window.location.search);
    const countryCode = urlParams.get('country') || 'VN';
    if (data.products) {
      data.products.forEach((p: any) => {
        if (p.prices) {
          const cp = p.prices.find((pr: any) => pr.country === countryCode);
          if (cp) p.price = cp.price_min || cp.price || p.price;
        }
      });
    }
    return data;
  }

  protected debounce<T extends (...args: any[]) => any>(func: T, wait: number) {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    return (...args: Parameters<T>) => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  protected formatProductPrices(model: TierDiscountModel): void {
    if (!this.container) return;

    // Format individual product prices
    model.properties.products.forEach((product: any) => {
      const priceEl = this.container?.querySelector(
        `.boost-sd__tier-discount-product[data-product-id="${product.id}"] .boost-sd__tier-discount-product-price`
      );
      if (priceEl && product.price) {
        priceEl.textContent = this.appService.formatCurrency(product.price);
      }
    });
  }

  /**
   * Hides the chevron SVG on the Add button for products that have only a single
   * "Default Title" variant — clicking the button adds immediately without needing
   * a dropdown, so the arrow indicator is misleading.
   *
   * @param container - Root element containing all tier-discount product rows.
   */
  protected hideArrowForSingleDefaultVariants(container: HTMLElement): void {
    container
      .querySelectorAll<HTMLElement>('.boost-sd__tier-discount-product')
      .forEach((productEl) => {
        const items = productEl.querySelectorAll<HTMLElement>(
          '.boost-sd__tier-discount-dropdown-item'
        );
        if (items.length !== 1) return;

        const title = items[0].dataset.variantTitle ?? '';
        if (title.toLowerCase() !== 'default title') return;

        const arrow = productEl.querySelector<SVGElement>(
          '.boost-sd__tier-discount-add-dropdown svg'
        );
        if (arrow) {
          arrow.style.display = 'none';
        }
      });
  }

  protected getProductId(): number | undefined {
    return (
      (window as any).boostSDData?.product?.id ||
      (window as any).boostWidgetIntegration?.generalSettings?.product_id
    );
  }

  protected getLocale(): string {
    return (window as any).Shopify?.routes?.root || '/';
  }

  /**
   * Safely set HTML content to avoid XSS vulnerabilities
   */
  protected safeSetHTML(container: Element, htmlContent: string): void {
    container.textContent = '';
    if (!htmlContent) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const elements = doc.body.children;
    Array.from(elements).forEach((element) => {
      container.appendChild(element.cloneNode(true));
    });
  }

  protected hasOnlyDefaultTitleVariant(productId: string | undefined): boolean {
    if (!productId || !this.state.value.model) return false;

    const product = this.state.value.model.properties.products.find(
      (p: any) => p.id.toString() === productId
    );

    if (!product || !product.variants) return false;

    // Check if product has only one variant and it's "Default Title"
    return (
      product.variants.length === 1 &&
      (product.variants[0].title === 'Default Title' ||
        product.variants[0].title === '' ||
        !product.variants[0].title)
    );
  }
}
